import {useQuery} from '@apollo/client';
import {Color, Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {Box, Group, Stack, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import type {ExtensionVersions, ExtensionVersionsVariables} from 'extensions/__generated/ExtensionVersions';
import {VERSION_TAGS_BY_EXTENSION_TYPE} from 'extensions/content-store/extensionConstants';
import {EXTENSION_VERSIONS_QUERY} from 'extensions/ExtensionQueries';
import type {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications, {Response} from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import * as modals from 'util/Modals';
import * as net from 'util/Net';

const EXTENSION_URL_BASE = '/1/api/extension-definitions';

type VersionTagsMap = Record<string, Array<string>>;

function parseVersionTags(raw: unknown): VersionTagsMap {
  if (raw == null) {
    return {};
  }
  if (typeof raw === 'object') {
    return raw as unknown as VersionTagsMap;
  }
  try {
    return JSON.parse(raw as string) as VersionTagsMap;
  } catch (err) {
    if (err instanceof SyntaxError) {
      return {};
    }
    throw err;
  }
}

function buildTagToVersionMap(versionTags: VersionTagsMap): Map<string, string> {
  const map = new Map<string, string>();
  for (const [versionId, tags] of Object.entries(versionTags)) {
    for (const tag of tags) {
      map.set(tag, versionId);
    }
  }
  return map;
}

export interface VersionsTabProps {
  extensionDefinitionId: string;
  extensionType: ExtensionType;
  editable?: boolean;
}

export default function VersionsTab({
  extensionDefinitionId,
  extensionType,
  editable = false,
}: VersionsTabProps): JSX.Element {
  const {data, loading, error, refetch} = useQuery<ExtensionVersions, ExtensionVersionsVariables>(
    EXTENSION_VERSIONS_QUERY,
    {
      variables: {extensionDefinitionId},
      fetchPolicy: 'network-only',
    }
  );
  const [saving, setSaving] = useState(false);

  const availableTags = VERSION_TAGS_BY_EXTENSION_TYPE[extensionType] ?? [];

  const versionTags: VersionTagsMap = useMemo(
    () => parseVersionTags(data?.extensionVersions?.versionTags),
    [data?.extensionVersions?.versionTags]
  );

  const tagToVersion = useMemo(() => buildTagToVersionMap(versionTags), [versionTags]);

  const versions = useMemo(() => data?.extensionVersions?.items ?? [], [data?.extensionVersions?.items]);

  const applyTag = useCallback(
    async (tag: string, targetVersionId: string | null) => {
      setSaving(true);
      try {
        await net.postJSONPromise({
          url: `${EXTENSION_URL_BASE}/${extensionDefinitionId}:set-version-tag`,
          data: {tag, versionId: targetVersionId},
        });
        await refetch();
      } catch (err: unknown) {
        if (net.isServerRequestError(err)) {
          notifications.notifyError(err as Response);
        } else {
          throw err;
        }
      } finally {
        setSaving(false);
      }
    },
    [extensionDefinitionId, refetch]
  );

  const handleTagToggled = useCallback(
    async (versionId: string, versionLabel: string, tag: string, isCurrentlyAssigned: boolean) => {
      if (isCurrentlyAssigned) {
        const confirmed = (await Promise.resolve(
          modals.confirm('Remove version tag', `Remove ${tag} from ${versionLabel}?`, ButtonStyle.PRIMARY, {
            acceptText: 'Confirm',
            rejectText: 'Cancel',
            enableRedesign: true,
            promiseCompatible: true,
          })
        )) as boolean;
        if (confirmed) {
          await applyTag(tag, null);
        }
      } else {
        const currentOwner = tagToVersion.get(tag) ?? null;
        const message =
          currentOwner != null
            ? `${tag} is currently assigned to ${versions.find((v) => v.id === currentOwner)?.versionId ?? currentOwner}. Move it to ${versionLabel}?`
            : `Assign ${tag} to ${versionLabel}?`;

        const confirmed = (await Promise.resolve(
          modals.confirm('Update version tag', message, ButtonStyle.PRIMARY, {
            acceptText: 'Confirm',
            rejectText: 'Cancel',
            enableRedesign: true,
            promiseCompatible: true,
          })
        )) as boolean;
        if (confirmed) {
          await applyTag(tag, versionId);
        }
      }
    },
    [tagToVersion, versions, applyTag]
  );

  if (loading) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed">
          Loading versions...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed">
          Error loading versions: {error.message}
        </Text>
      </Box>
    );
  }

  if (versions.length === 0) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed">
          No versions available.
        </Text>
      </Box>
    );
  }

  return (
    <Stack spacing={0}>
      {versions.map((version) => {
        const tags = versionTags[version.id] ?? [];
        const tagsSet = new Set(tags);
        return (
          <Group key={version.id} className={css(styles.versionRow)} position="apart" noWrap={true}>
            <Group spacing="sm" noWrap={true}>
              <Text size="sm" fw={600}>
                {version.versionId}
              </Text>
              {tags.map((tag) => (
                <Tag key={tag} text={tag} size={TagSize.SMALL} color={TagColor.SECONDARY} />
              ))}
            </Group>
            <Group spacing="sm" noWrap={true}>
              {editable && availableTags.length > 0 && (
                <>
                  {tags.map((tag) => (
                    <Button
                      key={`remove-${tag}`}
                      text={`Remove ${tag}`}
                      buttonStyle={ButtonStyle.NEGATIVE}
                      size={ButtonSize.SMALL}
                      disabled={saving}
                      onClick={() => {
                        handleTagToggled(version.id, version.versionId, tag, true);
                      }}
                    />
                  ))}
                  {availableTags.some((tag) => !tagsSet.has(tag)) && (
                    <Dropdown
                      closeOnInsideClick={true}
                      closeOnOutsideClick={true}
                      menu={
                        <Menu width={Menu.WIDTHS.MEDIUM} height={Menu.HEIGHTS.MEDIUM}>
                          <Section>
                            {availableTags
                              .filter((tag) => !tagsSet.has(tag))
                              .map((tag) => (
                                <Option
                                  key={tag}
                                  onSelect={() => {
                                    handleTagToggled(version.id, version.versionId, tag, false);
                                  }}
                                >
                                  {tag}
                                </Option>
                              ))}
                          </Section>
                        </Menu>
                      }
                    >
                      <Button
                        text="Set tag"
                        buttonStyle={ButtonStyle.TERTIARY_GRAY}
                        size={ButtonSize.SMALL}
                        isDropdown={true}
                        disabled={saving}
                      />
                    </Dropdown>
                  )}
                </>
              )}
            </Group>
          </Group>
        );
      })}
    </Stack>
  );
}

const styles = StyleSheet.create({
  versionRow: {
    padding: '10px 0',
    borderBottom: `1px solid ${Color.GRAY_200}`,
    ':last-child': {
      borderBottom: 'none',
    },
  },
});
